/**
 * Decimal money arithmetic.
 *
 * Every monetary value in this codebase crosses every boundary as a STRING —
 * `numeric(12,2)` columns, the API envelope, the typed clients, form state.
 * This module is where such strings are added, multiplied and compared, and it
 * is the only place that should do arithmetic on them.
 *
 * ## Why a decimal library rather than integer cents
 *
 * Integer cents is exact for two-decimal money, and the hand-rolled cents
 * helpers this module replaces were empirically correct. Two things pushed it
 * over:
 *
 *   1. **Multiplication squares the scale.** `qtyCents × rateCents` is a
 *      value in ten-thousandths, and it leaves `Number.MAX_SAFE_INTEGER`
 *      behind at a product around 9×10¹¹ — silently, producing a wrong figure
 *      rather than an error. `Big` has no such ceiling.
 *   2. **Rounding had to be hand-defended.** The previous `roundMoney` carried
 *      a `Number.EPSILON` correction to stop `1.005` landing on `1.00`. It
 *      worked, but it is a subtlety every future reader has to take on faith,
 *      and it only existed because the value had already been through a float.
 *
 * `Big` also rounds half-up by default (`Big.RM = 1`), which is what an
 * invoice means by rounding — bankers' rounding would make a customer's total
 * disagree with what they'd get on a calculator.
 *
 * ## The contract
 *
 * Inputs are strings (or `Big`). Outputs are canonical two-decimal strings.
 * Nothing here accepts or returns a JS `number`: a `number` parameter is
 * exactly the door through which float error gets in, and `Number('')` being
 * `0` is how a missing amount becomes a real $0.00 charge (G15).
 */

import Big from 'big.js';

/** Round half-up, the way an invoice means it. `Big`'s default is already 1
 *  (ROUND_HALF_UP), set explicitly so a future dependency bump that changes
 *  the default cannot quietly change everyone's totals. */
Big.RM = 1;

/** Scale of every monetary column in this codebase: `numeric(_, 2)`. */
export const MONEY_DP = 2;

/** A value this module can do arithmetic on. */
export type MoneyInput = string | Big;

/** Thrown when a string that must be money isn't. Callers that have a sensible
 *  fallback should use {@link isMoney} first rather than catching this. */
export class InvalidMoneyError extends Error {
  constructor(value: unknown, label = 'amount') {
    super(`${label} is not a valid decimal amount: ${JSON.stringify(value)}`);
    this.name = 'InvalidMoneyError';
  }
}

/**
 * Parse a money string.
 *
 * Rejects the empty string explicitly. `Big('')` throws anyway, but the reason
 * is worth stating: blank is the single most dangerous money input, because
 * `Number('')` is `0` and a missing amount would print as a real charge of
 * nothing.
 */
export function money(value: MoneyInput, label = 'amount'): Big {
  if (value instanceof Big) return value;
  if (typeof value !== 'string' || value.trim() === '') throw new InvalidMoneyError(value, label);
  try {
    return new Big(value.trim());
  } catch {
    throw new InvalidMoneyError(value, label);
  }
}

/** Whether `value` parses as money. For display paths that must degrade to
 *  "Unknown" rather than throw. */
export function isMoney(value: unknown): value is string {
  if (typeof value !== 'string' || value.trim() === '') return false;
  try {
    new Big(value.trim());
    return true;
  } catch {
    return false;
  }
}

/** Canonical two-decimal form: `'5'` → `'5.00'`, `'1.005'` → `'1.01'`.
 *  `toFixed` rather than `toString` so the value round-trips into a
 *  `numeric(_, 2)` column unchanged. */
export function toMoney(value: MoneyInput, label = 'amount'): string {
  return money(value, label).toFixed(MONEY_DP);
}

export function addMoney(a: MoneyInput, b: MoneyInput): string {
  return money(a).plus(money(b)).toFixed(MONEY_DP);
}

export function subtractMoney(a: MoneyInput, b: MoneyInput): string {
  return money(a).minus(money(b)).toFixed(MONEY_DP);
}

/**
 * `quantity × rate`, rounded to cents.
 *
 * The rounding happens once, here, at the end — not on the operands. This is
 * the operation that used to overflow: the old integer-cents version computed
 * a ten-thousandths-scaled product that left safe-integer range around
 * `qty × rate > 9×10¹¹`.
 */
export function multiplyMoney(quantity: MoneyInput, rate: MoneyInput): string {
  return money(quantity, 'quantity').times(money(rate, 'rate')).toFixed(MONEY_DP);
}

/**
 * Apply a percentage, e.g. `percentOfMoney('2864.25', '10')` → `'286.43'`.
 *
 * Exists because a percentage discount is entered as a percentage and stored
 * as dollars — the invoice keeps a signed amount, never a rate (see
 * docs/invoices.md). Resolving it here means one rounding rule for it.
 */
export function percentOfMoney(amount: MoneyInput, percent: MoneyInput): string {
  return money(amount).times(money(percent, 'percent')).div(100).toFixed(MONEY_DP);
}

/** Sum, left to right. An empty list is `'0.00'` — the identity, not a
 *  missing value. */
export function sumMoney(values: Iterable<MoneyInput>): string {
  let acc = new Big(0);
  for (const v of values) acc = acc.plus(money(v));
  return acc.toFixed(MONEY_DP);
}

export function negateMoney(value: MoneyInput): string {
  return money(value).times(-1).toFixed(MONEY_DP);
}

/** `-1`, `0`, `1`. Use instead of comparing formatted strings, where
 *  `'9.00' > '10.00'` lexicographically. */
export function compareMoney(a: MoneyInput, b: MoneyInput): -1 | 0 | 1 {
  return money(a).cmp(money(b)) as -1 | 0 | 1;
}

export function isNegativeMoney(value: MoneyInput): boolean {
  return money(value).lt(0);
}

export function isZeroMoney(value: MoneyInput): boolean {
  return money(value).eq(0);
}

/** Whether the value has at most two decimal places — i.e. survives storage in
 *  a `numeric(_, 2)` column without being silently rounded by Postgres. */
export function hasMoneyScale(value: string): boolean {
  if (!isMoney(value)) return false;
  const b = new Big(value.trim());
  return b.eq(b.round(MONEY_DP));
}

/**
 * Split `total` into one part per weight, in proportion to the weights, with
 * the parts adding up to `total` exactly.
 *
 * This is how an invoice line is shared between accounts: a T&M line split by
 * the hours behind it, or a split rescaled when the line's amount changes
 * (weights = the current parts). Rounding each part to the cent on its own
 * would leave the parts a cent or two off the line; instead each part is
 * truncated to the cent and the cents left over go, one each, to the parts
 * with the largest truncated remainders (ties to the earlier part). That is
 * the largest-remainder method, and it is why the sum is exact by
 * construction rather than by luck.
 *
 * Weights must be non-negative. When they sum to zero there is no proportion
 * to follow, so the total is split as evenly as the cents allow. A negative
 * total (a discount line) splits the same way, with every part negative.
 */
export function allocateMoney(total: MoneyInput, weights: readonly MoneyInput[]): string[] {
  if (weights.length === 0) throw new Error('allocateMoney needs at least one weight');
  const cents = money(total, 'total').times(100).round(0, Big.roundHalfUp);
  const parsed = weights.map((w) => money(w, 'weight'));
  if (parsed.some((w) => w.lt(0))) throw new Error('allocateMoney weights must be non-negative');

  const sum = parsed.reduce((acc, w) => acc.plus(w), new Big(0));
  const effective = sum.eq(0) ? parsed.map(() => new Big(1)) : parsed;
  const denominator = sum.eq(0) ? new Big(parsed.length) : sum;

  const exact = effective.map((w) => cents.times(w).div(denominator));
  const parts = exact.map((x) => x.round(0, Big.roundDown));
  let left = cents.minus(parts.reduce((acc, p) => acc.plus(p), new Big(0)));

  const order = exact
    .map((x, i) => ({ i, frac: x.minus(parts[i] as Big).abs() }))
    .sort((a, b) => b.frac.cmp(a.frac) || a.i - b.i);
  const step = left.gt(0) ? 1 : -1;
  for (let k = 0; !left.eq(0); k++) {
    const target = order[k % order.length] as { i: number };
    parts[target.i] = (parts[target.i] as Big).plus(step);
    left = left.minus(step);
  }
  return parts.map((p) => p.div(100).toFixed(MONEY_DP));
}
